// The 1:1 offer — single source of truth for pricing, caps, and booking.
// Change numbers here; the /call page reads everything from this file.
//
// The Workflow Sprint deliberately does not appear on the site. It is offered
// at the end of a Review, once the repo has actually been seen. Publishing a
// price and scope for something that has never been delivered locks in a shape
// before there is any evidence for it.

export const CAL_BOOKING_URL = 'https://cal.com/darrenhinde/workflow-review'

export const OFFER_EMAIL =
  'mailto:hello@controlstack.ai?subject=Workflow%20Review%20booking&body=What%20you%27d%20like%20to%20work%20on%20(one%20sentence)%3A%0AWhat%20you%27ve%20tried%20so%20far%3A%0ARepo%20link%20(optional)%3A'

export const BOOKING_HREF = CAL_BOOKING_URL || OFFER_EMAIL

export const REVIEW = {
  name: 'Workflow Review',
  /** Display string. Derived from `priceAmount` — keep the two in step. */
  price: '£150',
  /**
   * The same number for structured data. Kept as its own field rather than
   * parsed back out of `price`, because the moment the display string becomes
   * a range or carries a qualifier ('From £150', '£150–£300') a regex over it
   * publishes a silently wrong price in the Offer schema.
   */
  priceAmount: 150,
  priceCurrency: 'GBP',
  duration: '90 minutes',
  capLine: 'Two sessions a week',
} as const

/**
 * The founding rate explains the price rather than leaving it to be read as
 * "no track record". Update `sessionsDone` as sessions are delivered; when it
 * reaches `foundingSessions`, retire this block and raise the price.
 */
export const FOUNDING = {
  active: true,
  foundingSessions: 10,
  note: 'Founding rate for the first ten sessions. It goes up after that — the sessions do not get worse, I just stop needing the first ten write-ups.',
} as const

export const INCLUDED = [
  'Ninety minutes on a screen-share, working on your actual situation — not a talk about workflows in general.',
  'If you bring a repo, we work in it: context files, agent instructions and review loop rewritten live, by me, while you watch.',
  'A written action plan typed during the call, in a shared doc you keep.',
  'The recording, if you want it.',
  'One follow-up email a week later if something we changed did not hold up.',
] as const

export const NOT_INCLUDED = [
  'I do not write your feature for you. We fix how the work gets done, not the backlog.',
  'No ongoing retainer, Slack access, or "unlimited questions" afterwards.',
  'Not a code audit. Ninety minutes is not enough to responsibly review a whole codebase for security or architecture.',
  'Not a tutorial. If you have never used an agent before, install OpenAgents Control and use it for a week first — you will get far more out of the session afterwards.',
] as const

export const GOOD_FIT = [
  'You already use Claude Code, Cursor, OpenCode or similar — or you are about to and want to start properly.',
  'You are working on something real: a codebase, a project you are planning, or a setup you want to get right.',
  'The tools work in demos but drift on your actual work.',
  'You spend longer reviewing generated code than writing it would have taken.',
] as const

export const BAD_FIT = [
  'You want someone to build the thing for you. That is contract work, not this.',
  'You are choosing between tools and want a recommendation. That is a free video, not £150.',
  'You want a general lecture on AI coding. Bring something specific — a repo, a setup, a decision you are stuck on — or ninety minutes will drift.',
  'You are brand new to programming. This assumes you can already read code.',
] as const

export const REFUND_POLICY =
  "If I can't help you, I'll say so in the first 15 minutes and refund you in full. No argument, no forms."

export const RESCHEDULE_POLICY =
  'Reschedule free up to 24 hours before. Inside 24 hours the slot is used.'

export const FAQ = [
  {
    q: 'Do I need to bring a repo?',
    a: 'No. Bringing one makes the session sharper, but plenty of good sessions are about a setup you are planning, an agent config that keeps misbehaving, or a decision you are stuck on. What matters is that you bring something real rather than a general question.',
  },
  {
    q: 'My code is private or under NDA. Does that rule this out?',
    a: 'Not at all. Most people screen-share rather than send anything, so nothing gets cloned or kept. If even screen-sharing is off the table, we can work on a redacted branch, a reproduction, or just your setup and your approach — and it is still a useful ninety minutes.',
  },
  {
    q: 'What if my problem is not one of the ones you list?',
    a: 'Send it to me before booking. If it is not something I can help with, I will say so and not take the money — that is easier for both of us than finding out fifteen minutes in.',
  },
  {
    q: 'What actually happens in the ninety minutes?',
    a: 'The first fifteen are you showing me the thing that is breaking. Then we work on it — I drive as much as you do, and I change files. The last ten minutes we write down what you are doing next. There is no presentation and no prep deck.',
  },
  {
    q: 'Why £150?',
    a: 'It is a founding rate for the first ten sessions, and the trade is that I get to write up what we found. It will go up. If you think it is cheap because I have no track record, watch a few videos first and decide from that rather than from the number.',
  },
  {
    q: 'What if it is not useful?',
    a: 'Then you get your money back. I will tell you inside the first fifteen minutes if I do not think I can help, and refund you in full. If it goes the distance and still misses, email me and I will refund you anyway — I would rather that than have you tell people it was a waste.',
  },
  {
    q: 'Why only two a week?',
    a: 'Because I still build and ship with these tools daily, and that is the part that makes the advice worth anything. If the calendar is full, it is genuinely full rather than a countdown timer.',
  },
  {
    q: 'Do I get a recording?',
    a: 'If you want one, yes. Say so at the start of the call and I will record it and send it over afterwards. It is your call, not the default.',
  },
  {
    q: 'What happens after the session?',
    a: 'You leave with the written plan and go do it. A week later I will email once to ask whether the changes held up. If you want more sessions after that, we can talk about it then — there is nothing to sign up for now.',
  },
] as const
